import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { FormService } from 'app/services/system/form/form.service';
import { USER_ROLES } from 'common/constants/user-role.constants';
import { Observable, Subject, filter, takeUntil } from 'rxjs';

@Component({
    selector: 'app-settings-users-user',
    templateUrl: './settings-users-user.component.html',
    styleUrls: ['./settings-users-user.component.scss'],
})
export class SettingsUsersUserComponent implements OnInit {
    //#region Keypresses and before leave

    @HostListener('window:beforeunload')
    canDeactivate(): Observable<boolean> | boolean {
        try {
            let hasNoFormChanges = this.profileForm.dirty;
            return !hasNoFormChanges;
        } catch (e) {
            return true;
        }
    }

    //#endregion

    //#region Globals

    // Forms
    private profileForm: UntypedFormGroup;

    beforeLeaveMessage = this.translationService.translate('info_leave_form_message');
    panels: any[] = [];
    selectedPanel: string = 'profile';
    selectedComponent: string = 'app-user-settings-profile';
    userDetails: ExposedReadUserDetailsRes = null;

    //#endregion

    //#region Init

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private formService: FormService,
        private location: Location,
        private route: ActivatedRoute,
        private router: Router,
        public lsrauthService: LsrauthService,
        private translationService: TranslationService
    ) {
        this.formService.profileForm.subscribe((profileForm) => (this.profileForm = profileForm));
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.initPanel();
    }

    ngAfterViewInit(): void {}

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    //#endregion

    //#region FUSE

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    @ViewChild('drawer') drawer: MatDrawer;
    drawerMode: 'over' | 'side' = 'side';
    drawerOpened: boolean = true;

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    //#endregion

    //#region Panel

    initPanel() {
        // Setup available panels
        this.panels = [
            {
                id: 'profill',
                component: 'app-settings-users-user-profile',
                icon: 'heroicons_outline:user-circle',
                title: this.translationService.translate('label_profile'),
                description: this.translationService.translate('label_general_information'),
            },
        ];

        this.lsrauthService.fetchUserDetails(true).subscribe((user) => {
            this.selectedPanel = this.route.snapshot.paramMap.get('panel');
            this.selectedComponent = this.panels.find((x) => x.id == this.route.snapshot.paramMap.get('panel')).component;

            // Subscribe to media changes
            this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
                // Set the drawerMode and drawerOpened
                if (matchingAliases.includes('lg')) {
                    this.drawerMode = 'side';
                    this.drawerOpened = true;
                } else {
                    this.drawerMode = 'over';
                    this.drawerOpened = false;
                }

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

            // Makes sure to allow user to switch tabs from menu panel (username)
            this.router.events.pipe(filter((event: any) => event instanceof NavigationEnd)).subscribe((x) => {
                this.selectedPanel = this.route.snapshot.paramMap.get('panel');
                this.selectedComponent = this.panels.find((x) => x.id == this.route.snapshot.paramMap.get('panel')).component;
            });

            // Add the roles panel for employees only
            if (this.lsrauthService.inRole(this.userDetails, USER_ROLES.EMPLOYEE_LSR)) {
                this.panels.push({
                    id: 'adgangsheimildir',
                    component: 'app-settings-users-user-roles',
                    icon: 'heroicons_outline:shield-check',
                    title: this.translationService.translatePlural('label_access_role'),
                    description: this.translationService.translatePlural('label_your_access_role'),
                });
            }
        });
    }

    /**
     * Navigate to the panel. Checks if something has been editied within the associated forms.
     *
     * @param panel
     */
    goToPanel(panel: string, component: string): void {
        if (this.profileForm && this.profileForm.dirty) {
            // If changes have been made to forms, make sure to ask before leaving page without save.
            if (confirm(this.beforeLeaveMessage)) {
                this.proceedPanel(panel, component);
            }
        } else {
            this.proceedPanel(panel, component);
        }
    }

    proceedPanel(panel: string, component: string): void {
        this.location.go('/stillingar/' + panel);
        this.clearTouchForms();
        this.selectedPanel = panel;
        this.selectedComponent = component;

        // Close the drawer on 'over' mode
        if (this.drawerMode === 'over') {
            this.drawer.close();
        }
    }

    // When switching between panels we mark all forms as untouched.
    clearTouchForms() {
        try {
            this.profileForm.markAsPristine();
        } catch {}
    }

    /**
     * Get the details of the panel
     *
     * @param id
     */
    getPanelInfo(id: string): any {
        return this.panels?.find((panel) => panel.id === id) ?? '';
    }

    getPanelTitle(id: string): any {
        return this.panels?.find((panel) => panel.id === id)?.title ?? '';
    }

    getPanelDescription(id: string): any {
        return this.panels?.find((panel) => panel.id === id)?.description ?? '';
    }

    //#endregion
}
